import { Component, DestroyRef, inject, Injector, viewChild, ElementRef, AfterViewInit, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { ReactiveFormsModule, FormsModule, Validators, FormBuilder, FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule, MatChipInputEvent } from '@angular/material/chips';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { GalaxyTooltipModule } from '@vendasta/galaxy/tooltip';
import { PopoverPositions } from '@vendasta/galaxy/popover';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { BreakpointObserver } from '@angular/cdk/layout';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyRichTextEditorModule } from '@vendasta/galaxy/rich-text-editor';
import { GalaxyButtonLoadingIndicatorModule } from '@vendasta/galaxy/button-loading-indicator';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { OpenConfirmationModalService } from '@vendasta/galaxy/confirmation-modal';
import { firstValueFrom, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import {
  SendApiService,
  SendFromProviderRequest,
  SendFromProviderRequestCrmObjectAssociation,
  Provider,
  UserParticipant,
  ContactParticipant,
  EmailParticipant,
  EmailRecipient,
  WhiteLabelEmailMessage,
  Visibility,
  SourceInfo,
} from '@vendasta/email';
import { Contact, ContactProvider } from '@galaxy/email-ui/shared';
import { CrmFieldService, StandardExternalIds } from '@galaxy/crm/services/field-service';
import {
  SelectObjectTypeModalComponent,
  ListObjectTypeDialogData,
  ListObjectTypeDialogResult,
  PageAnalyticsInjectionToken,
  tableFiltersInjectionTokenGenerator,
  pageAnalyticsInjectionTokenGenerator,
} from '@galaxy/crm/static';
import { GalaxyFilterChipInjectionToken } from '@vendasta/galaxy/filter/chips';
import {
  DEFAULT_RICH_TEXT_PLUGINS,
  DEFAULT_RICH_TEXT_TOOLBAR,
  EmailProvider,
  EMAIL_PROVIDER_OUTLOOK,
} from './constants';
import { EmailComposerData, ProviderConnectionOption } from '../email-workflow.service';
import { SendEmailService } from './send-email.service';

interface ContactInfo {
  id: string;
  name: string;
  email: string;
}

const MAX_RECIPIENTS = 100;
const EMAIL_REGEX =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?)+$/;

@Component({
  selector: 'lib-crm-send-email',
  templateUrl: './send-email.component.html',
  styleUrls: ['./send-email.component.scss'],
  imports: [
    ReactiveFormsModule,
    FormsModule,
    TranslateModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatChipsModule,
    MatAutocompleteModule,
    GalaxyFormFieldModule,
    GalaxyTooltipModule,
    GalaxyRichTextEditorModule,
    GalaxyButtonLoadingIndicatorModule,
  ],
})
export class CrmSendEmailComponent implements OnInit, AfterViewInit {
  private readonly dialogRef = inject(MatDialogRef<CrmSendEmailComponent>);
  private readonly dialogData = inject<EmailComposerData>(MAT_DIALOG_DATA);
  private readonly matDialog = inject(MatDialog);
  private readonly snackbarService = inject(SnackbarService);
  private readonly confirmationModalService = inject(OpenConfirmationModalService);
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly fb = inject(FormBuilder);
  private readonly sendEmailService = inject(SendApiService);
  private readonly draftService = inject(SendEmailService);
  private readonly router = inject(Router);
  private readonly translateService = inject(TranslateService);

  protected readonly visibilityOptions = [
    { value: Visibility.VISIBILITY_PRIVATE, label: 'CRM.VISIBILITY_PRIVATE' },
    { value: Visibility.VISIBILITY_INTERNAL, label: 'CRM.VISIBILITY_INTERNAL' },
  ];

  protected readonly tooltipPositions = [PopoverPositions['Right'], PopoverPositions['Top']];
  readonly subjectInput = viewChild<ElementRef<HTMLInputElement>>('subjectInput');

  protected readonly DEFAULT_RICH_TEXT_PLUGINS = DEFAULT_RICH_TEXT_PLUGINS;
  protected readonly DEFAULT_RICH_TEXT_TOOLBAR = DEFAULT_RICH_TEXT_TOOLBAR;

  protected readonly showErrors = signal(false);
  protected readonly isLoading = signal(false);
  protected readonly isSending = signal(false);
  protected readonly contacts = signal<ContactInfo[]>([]);
  protected readonly recipientLimitError = signal(false);
  private emailSent = false;
  private readonly destroyRef = inject(DestroyRef);
  private readonly MOBILE_BREAKPOINT = '(max-width: 599px)';

  protected readonly connections: ProviderConnectionOption[] = this.dialogData.connections || [];
  protected readonly selectedConnectionId = signal(this.connections[0]?.connectionId ?? '');

  protected readonly emailForm = this.fb.nonNullable.group({
    subject: ['', [Validators.required]],
    content: ['', [Validators.required]],
    visibility: [Visibility.VISIBILITY_INTERNAL],
  });

  protected readonly toChips = signal<string[]>([]);
  protected readonly showCc = signal(false);
  protected readonly showBcc = signal(false);
  protected readonly ccChips = signal<string[]>([]);
  protected readonly bccChips = signal<string[]>([]);
  protected readonly ccSearchResults = signal<Contact[]>([]);
  protected readonly bccSearchResults = signal<Contact[]>([]);
  protected readonly ccSearchControl = new FormControl<string>('', { nonNullable: true });
  protected readonly bccSearchControl = new FormControl<string>('', { nonNullable: true });
  protected readonly separatorKeyCodes = [ENTER, COMMA] as const;

  private readonly injector = inject(Injector);
  private readonly crmFieldService = inject(CrmFieldService, { optional: true });

  private readonly contactProvider: ContactProvider | undefined = this.dialogData.contactProvider;

  constructor() {
    if (this.dialogData?.contactIds?.length) {
      this.loadAndPopulateContacts(this.dialogData.contactIds, this.dialogData.emails || []);
    }

    if (this.dialogData?.replySubject) {
      this.emailForm.patchValue({ subject: this.dialogData.replySubject });
    }

    if (this.dialogData?.replyVisibility) {
      const v = this.dialogData.replyVisibility.toUpperCase();
      if (v === 'PRIVATE') {
        this.emailForm.patchValue({ visibility: Visibility.VISIBILITY_PRIVATE });
      } else if (v === 'INTERNAL') {
        this.emailForm.patchValue({ visibility: Visibility.VISIBILITY_INTERNAL });
      }
    }

    this.setupAutocomplete();
    this.destroyRef.onDestroy(() => this.saveDraftIfNeeded());
  }

  ngOnInit(): void {
    this.breakpointObserver
      .observe(this.MOBILE_BREAKPOINT)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((state) => {
        this.updateDialogSize(state.matches);
      });
  }

  ngAfterViewInit() {
    const inputElement = this.subjectInput();
    if (inputElement) {
      setTimeout(() => {
        inputElement.nativeElement.focus();
      }, 0);
    }
  }

  private setupAutocomplete(): void {
    const provider = this.contactProvider;
    if (!provider) {
      return;
    }
    this.bindContactSearch(provider, this.ccSearchControl, this.ccSearchResults);
    this.bindContactSearch(provider, this.bccSearchControl, this.bccSearchResults);
  }

  private bindContactSearch(
    provider: ContactProvider,
    control: FormControl<string>,
    results: typeof this.ccSearchResults,
  ): void {
    control.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((term) => {
          if (!term || term.length < 2) {
            return of([]);
          }
          return provider.searchContacts(term, '', 10).pipe(
            map((result) => result.contacts),
            catchError(() => of([])),
          );
        }),
        map((contacts) => this.filterExistingRecipients(contacts)),
      )
      .subscribe((contacts) => results.set(contacts));
  }

  private filterExistingRecipients(contacts: Contact[]): Contact[] {
    const existing = new Set([
      ...this.toChips().map((e) => e.toLowerCase()),
      ...this.ccChips().map((e) => e.toLowerCase()),
      ...this.bccChips().map((e) => e.toLowerCase()),
    ]);
    return contacts.filter((c) => c.email && !existing.has(c.email.toLowerCase()));
  }

  private isInTarget(email: string, target: 'to' | 'cc' | 'bcc'): boolean {
    const lower = email.toLowerCase();
    const chips = target === 'to' ? this.toChips() : target === 'cc' ? this.ccChips() : this.bccChips();
    return chips.some((e) => e.toLowerCase() === lower);
  }

  private findEmailSource(email: string): 'to' | 'cc' | 'bcc' | null {
    const lower = email.toLowerCase();
    if (this.toChips().some((e) => e.toLowerCase() === lower)) return 'to';
    if (this.ccChips().some((e) => e.toLowerCase() === lower)) return 'cc';
    if (this.bccChips().some((e) => e.toLowerCase() === lower)) return 'bcc';
    return null;
  }

  private tryMoveOrAdd(email: string, target: 'to' | 'cc' | 'bcc'): boolean {
    if (this.isInTarget(email, target)) {
      return false;
    }
    const lower = email.toLowerCase();
    const source = this.findEmailSource(email);
    if (source) {
      if (source === 'to' && this.toChips().length <= 1) {
        this.snackbarService.openErrorSnack('CRM.CANNOT_MOVE_LAST_TO_RECIPIENT');
        return false;
      }
      const removeFilter = (e: string) => e.toLowerCase() !== lower;
      if (source === 'to') this.toChips.update((c) => c.filter(removeFilter));
      else if (source === 'cc') this.ccChips.update((c) => c.filter(removeFilter));
      else this.bccChips.update((c) => c.filter(removeFilter));
    }
    if (target === 'to') this.toChips.update((c) => [...c, email]);
    else if (target === 'cc') this.ccChips.update((c) => [...c, email]);
    else this.bccChips.update((c) => [...c, email]);
    return true;
  }

  protected removeToChip(email: string): void {
    if (this.toChips().length <= 1) {
      return;
    }
    this.toChips.update((chips) => chips.filter((c) => c !== email));
    this.recipientLimitError.set(false);
  }

  protected canRemoveToChip(): boolean {
    return this.toChips().length > 1;
  }

  protected openContactSelector(target: 'to' | 'cc' | 'bcc'): void {
    if (this.isSending()) {
      return;
    }

    const modalInjector = Injector.create({
      providers: [
        {
          provide: PageAnalyticsInjectionToken,
          useFactory: pageAnalyticsInjectionTokenGenerator('Contact'),
        },
        {
          provide: GalaxyFilterChipInjectionToken,
          useFactory: tableFiltersInjectionTokenGenerator('Contact', undefined, false),
        },
      ],
      parent: this.injector,
    });

    const ref = this.matDialog.open(SelectObjectTypeModalComponent, {
      data: {
        baseColumnIds: [],
        objectType: 'Contact',
        modalTitle: this.translateService.instant('CRM.SELECT_CONTACTS'),
        associations: [],
        associationType: '',
        newSelection: '',
        hideCreateButton: true,
        showActions: false,
        hideActionButton: true,
      } as ListObjectTypeDialogData,
      width: '900px',
      maxHeight: '80vh',
      autoFocus: 'input',
      injector: modalInjector,
    });

    ref.afterClosed().subscribe((result: ListObjectTypeDialogResult | undefined) => {
      if (!result?.selectedRows?.length) {
        return;
      }
      const emailFieldId = this.crmFieldService?.getFieldId(StandardExternalIds.Email) ?? '';
      for (const row of result.selectedRows) {
        const email = row.data[emailFieldId]?.value || '';
        if (!email) {
          continue;
        }
        if (this.tryMoveOrAdd(email, target)) {
          this.contacts.update((existing) => {
            if (existing.some((c) => c.email === email)) {
              return existing;
            }
            return [...existing, { id: row.id, name: row.data['fullName']?.value || row.id, email }];
          });
        }
      }
      this.recipientLimitError.set(false);
    });
  }

  protected toggleCc(): void {
    this.showCc.update((v) => !v);
    if (!this.showCc()) {
      this.ccChips.set([]);
      this.ccSearchControl.setValue('');
    }
  }

  protected toggleBcc(): void {
    this.showBcc.update((v) => !v);
    if (!this.showBcc()) {
      this.bccChips.set([]);
      this.bccSearchControl.setValue('');
    }
  }

  protected addCcChip(event: MatChipInputEvent): void {
    const email = (event.value || '').trim();
    if (email && EMAIL_REGEX.test(email)) {
      this.tryMoveOrAdd(email, 'cc');
    }
    event.chipInput.clear();
    this.ccSearchControl.setValue('');
    this.recipientLimitError.set(false);
  }

  protected removeCcChip(email: string): void {
    this.ccChips.update((chips) => chips.filter((c) => c !== email));
    this.recipientLimitError.set(false);
  }

  protected selectCcContact(event: MatAutocompleteSelectedEvent): void {
    const contact = event.option.value as Contact;
    if (contact.email) {
      this.tryMoveOrAdd(contact.email, 'cc');
    }
    this.ccSearchControl.setValue('');
  }

  protected addBccChip(event: MatChipInputEvent): void {
    const email = (event.value || '').trim();
    if (email && EMAIL_REGEX.test(email)) {
      this.tryMoveOrAdd(email, 'bcc');
    }
    event.chipInput.clear();
    this.bccSearchControl.setValue('');
    this.recipientLimitError.set(false);
  }

  protected removeBccChip(email: string): void {
    this.bccChips.update((chips) => chips.filter((c) => c !== email));
    this.recipientLimitError.set(false);
  }

  protected selectBccContact(event: MatAutocompleteSelectedEvent): void {
    const contact = event.option.value as Contact;
    if (contact.email) {
      this.tryMoveOrAdd(contact.email, 'bcc');
    }
    this.bccSearchControl.setValue('');
  }

  private getTotalRecipientCount(): number {
    return this.toChips().length + this.ccChips().length + this.bccChips().length;
  }

  private updateDialogSize(isMobile: boolean): void {
    if (isMobile) {
      this.dialogRef.updateSize('100vw', '100vh');
    } else {
      this.dialogRef.updateSize('1000px', '95vh');
      this.dialogRef.updatePosition();
    }
  }

  private async loadAndPopulateContacts(contactIds: string[], emailAddresses: string[]): Promise<void> {
    try {
      this.isLoading.set(true);

      this.contacts.set(
        contactIds.map((id, index) => ({
          id,
          name: `Contact ${index + 1}`,
          email: emailAddresses[index] || '',
        })),
      );

      const validEmails = this.contacts()
        .map((contact) => contact.email)
        .filter((email) => email && email.trim() !== '');

      if (validEmails.length === 0) {
        await firstValueFrom(
          this.confirmationModalService.openModal({
            type: 'warn',
            title: 'CRM.NO_EMAIL_ADDRESS_TITLE',
            message: 'CRM.NO_EMAIL_ADDRESS_MESSAGE',
            confirmButtonText: 'COMMON.ACTION_LABELS.CLOSE',
            cancelButtonText: undefined,
            cancelOnEscapeKeyOrBackgroundClick: true,
          }),
        );
        this.closeModal();
        return;
      }

      this.toChips.set(validEmails);

      if (!this.dialogData?.isReply) {
        await this.checkAndRestoreDraft(validEmails.join(', '));
      }
    } catch (error) {
      console.error('Error loading contacts:', error);
      this.snackbarService.openErrorSnack('CRM.ERROR_LOADING_CONTACTS');
    } finally {
      this.isLoading.set(false);
    }
  }

  private async checkAndRestoreDraft(to: string): Promise<void> {
    const draft = this.draftService.getDraft(to);
    if (!draft) {
      return;
    }

    const shouldRestore = await firstValueFrom(this.draftService.askToRestoreDraft(to));

    if (shouldRestore) {
      this.emailForm.patchValue({
        subject: draft.subject,
        content: draft.content,
      });
      if (draft.cc.length > 0) {
        this.ccChips.set(draft.cc);
        this.showCc.set(true);
      }
      if (draft.bcc.length > 0) {
        this.bccChips.set(draft.bcc);
        this.showBcc.set(true);
      }
    } else {
      this.draftService.deleteDraft(to);
    }
  }

  private saveDraftIfNeeded(): void {
    if (this.emailSent) {
      return;
    }

    const toValue = this.toChips().join(', ');
    const subject = this.emailForm.value.subject || '';
    const content = this.emailForm.value.content || '';

    if (toValue && (subject.trim() || content.trim() || this.ccChips().length > 0 || this.bccChips().length > 0)) {
      this.draftService.saveDraft(toValue, subject, content, this.ccChips(), this.bccChips());
    }
  }

  protected async onSend() {
    if (this.toChips().length === 0) {
      this.showErrors.set(true);
      return;
    }

    if (!this.emailForm.valid) {
      this.showErrors.set(true);
      this.emailForm.markAllAsTouched();
      return;
    }

    if (this.getTotalRecipientCount() > MAX_RECIPIENTS) {
      this.recipientLimitError.set(true);
      return;
    }

    if (!this.isSending()) {
      try {
        this.isSending.set(true);

        const formValue = this.emailForm.getRawValue();
        await this.sendEmailWithProvider(
          formValue.subject,
          formValue.content,
          this.dialogData.provider,
          formValue.visibility,
        );

        this.emailSent = true;
        const toValue = this.toChips().join(', ');
        if (toValue) {
          this.draftService.deleteDraft(toValue);
        }

        this.snackbarService.openSuccessSnack('CRM.EMAIL_SENT_SUCCESS');
        this.closeModal();
      } catch (error) {
        console.error('Error sending email:', error);
        this.snackbarService.openErrorSnack('CRM.ERROR_SENDING_EMAIL');
      } finally {
        this.isSending.set(false);
      }
    }
  }

  private async sendEmailWithProvider(
    subject: string,
    content: string,
    provider: EmailProvider,
    visibility: Visibility,
  ): Promise<void> {
    const request = this.buildSendFromProviderRequest(
      this.toChips(),
      this.ccChips(),
      this.bccChips(),
      this.dialogData.namespace,
      this.dialogData.userId,
      subject,
      content,
      provider,
      visibility,
    );
    await firstValueFrom(this.sendEmailService.sendFromProvider(request));
  }

  private buildEmailRecipients(emails: string[]): EmailRecipient[] {
    return emails
      .filter((e) => e)
      .map((email) => {
        const contact = this.contacts().find((c) => c.email === email);
        if (contact) {
          return new EmailRecipient({
            contactParticipant: new ContactParticipant({
              contactId: contact.id,
              namespace: this.dialogData.namespace,
            }),
          });
        }
        return new EmailRecipient({
          emailParticipant: new EmailParticipant({ email }),
        });
      });
  }

  private buildSendFromProviderRequest(
    toEmails: string[],
    ccEmails: string[],
    bccEmails: string[],
    namespace: string,
    currentUserId: string,
    subject: string,
    htmlContent: string,
    provider: EmailProvider,
    visibility: Visibility,
  ): SendFromProviderRequest {
    const userParticipant = new UserParticipant({
      userId: currentUserId,
    });

    let fullHtmlContent = htmlContent;
    if (this.dialogData?.replyBody) {
      fullHtmlContent += `<br><br><blockquote style="border-left:2px solid #ccc;padding-left:8px;margin-left:0;color:#555">${this.dialogData.replyBody}</blockquote>`;
    }

    const message = new WhiteLabelEmailMessage({
      subject: subject,
      htmlContent: fullHtmlContent,
      attributes: [],
      links: [],
      attachments: [],
      urlAttachments: [],
    });

    const sourceInfo = new SourceInfo({
      sourceName: 'CRM UI',
      sourceDrillDown1: this.router.url,
    });

    const additionalAssociations = (this.dialogData.associations || [])
      .filter((a) => a.crmObjectId && a.crmObjectType)
      .map(
        (a) =>
          new SendFromProviderRequestCrmObjectAssociation({
            crmObjectId: a.crmObjectId,
            crmObjectType: a.crmObjectType,
            crmObjectSubtype: a.crmObjectSubtype,
            tags: a.tags,
          }),
      );

    return new SendFromProviderRequest({
      namespace: namespace,
      provider: provider === EMAIL_PROVIDER_OUTLOOK ? Provider.PROVIDER_OUTLOOK : Provider.PROVIDER_GMAIL,
      userParticipant: userParticipant,
      message: message,
      sendAsDraft: false,
      providerMessageThreadId: this.dialogData?.providerMessageThreadId || undefined,
      visibility: visibility,
      sourceInfo: sourceInfo,
      fromConnectionId: this.selectedConnectionId() || undefined,
      inReplyToMessageId: this.dialogData?.inReplyToMessageId || undefined,
      additionalAssociations: additionalAssociations.length > 0 ? additionalAssociations : undefined,
      toRecipients: this.buildEmailRecipients(toEmails),
      ccRecipients: this.buildEmailRecipients(ccEmails),
      bccRecipients: this.buildEmailRecipients(bccEmails),
    });
  }

  onCancel() {
    this.saveDraftIfNeeded();
    this.closeModal();
  }

  private closeModal() {
    this.dialogRef.close();
  }

}
